import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { toast } from "../ui/modals.js";

/**
 * Payment reminders (Rachit, 2026-10-15: "when a person forgets to pay, put a reminder on the screen").
 * GET /api/reminders/payments.
 *
 *  - a reminder card on every screen while a government instalment is due at the start of next
 *    month (red when your cash won't cover it), with Pay now; also what's due soon and anything
 *    just missed;
 *  - before Next Month: if a due instalment would be missed for lack of cash, ask first.
 * "Hide" hides the card until the next month (the check before Next Month still runs).
 */

let remData = null;
const REM_HIDE_KEY = "ges.reminder.hidden";

const remM = (v) => {
  const a = Math.abs(v || 0);
  return a >= 1e9 ? `$${(a / 1e9).toFixed(2)}B` : a >= 1e6 ? `$${(a / 1e6).toFixed(2)}M` : a >= 1e3 ? `$${(a / 1e3).toFixed(0)}K` : `$${Math.round(a)}`;
};

function remHidden(month) {
  try { return localStorage.getItem(REM_HIDE_KEY) === String(month); } catch (e) { return false; }
}

async function remPay(ids) {
  for (const id of ids) {
    try { toast((await postJSON("/actions/execute", { action: { type: "gov_pay_instalment", params: { payment_id: id } } })).message); }
    catch (e) { toast(e.message, true); return false; }
  }
  if (window.refreshAll) await window.refreshAll();
  return true;
}

function remPaint() {
  let el = document.getElementById("pay-reminder");
  const d = remData;
  const dash = document.getElementById("dashboard-screen");
  const show = d && dash && !dash.classList.contains("hidden") && (d.due_now.length || d.missed.length || d.soon.length) && !remHidden(d.month);
  if (!show) { if (el) el.remove(); return; }
  if (!el) {
    el = document.createElement("aside");
    el.id = "pay-reminder";
    el.setAttribute("role", "alert");
    document.body.appendChild(el);
  }
  const tone = d.missed.length || d.short ? "bad" : d.due_now.length ? "due" : "soon";
  el.className = `pay-rem ${tone}${tone === "bad" ? "" : " compact"}`;
  if (tone !== "bad") {
    // cash covers it: it's paid automatically, so one small line is enough
    const x = d.due_now[0] || d.soon[0];
    const more = d.due_now.length + (d.due_now.length ? 0 : d.soon.length) - 1;
    el.innerHTML = `<button type="button" class="pay-rem-line" data-rem-open title="Paid automatically from cash at the start of the month">💳 ${esc(x.country)} ${remM(x.amount)} ${d.due_now.length ? "due next month" : `due month ${x.due_month}`}${more > 0 ? ` +${more} more` : ""} · paid from cash</button>
      <button type="button" class="pay-rem-x" data-rem-hide aria-label="Hide until next month">✕</button>`;
    el.querySelector("[data-rem-hide]").addEventListener("click", () => { try { localStorage.setItem(REM_HIDE_KEY, String(d.month)); } catch (e) { /* not remembered */ } el.remove(); });
    el.querySelector("[data-rem-open]").addEventListener("click", () => window.ges && window.ges.showTab("government"));
    return;
  }
  el.innerHTML = `
    <button type="button" class="pay-rem-x" data-rem-hide aria-label="Hide until next month">✕</button>
    <div class="pay-rem-icon" aria-hidden="true">${d.missed.length ? "⚠" : "💳"}</div>
    <div class="pay-rem-main">
      <b>${d.missed.length ? "You missed a government payment" : d.due_now.length ? "You have to pay" : "Payment coming up"}</b>
      ${d.missed.map((x) => `<span>${esc(x.country)}: ${remM(x.amount)} was missed. Trust and brand fell and the deal's incentive was cancelled.</span>`).join("")}
      ${d.due_now.map((x) => `<span>${esc(x.country)}: <b>${remM(x.amount)}</b> is due at the start of next month.</span>`).join("")}
      ${!d.due_now.length ? d.soon.map((x) => `<span>${esc(x.country)}: ${remM(x.amount)} due in month ${x.due_month}.</span>`).join("") : ""}
      ${d.short ? `<span class="pay-rem-warn">Your cash is ${remM(d.cash)}: not enough. Raise cash before you end the month, or the payment is missed.</span>` : ""}
      <div class="pay-rem-go">
        ${d.due_now.length && !d.short ? `<button type="button" class="primary" data-rem-pay>Pay now (${remM(d.due_now_total)})</button>` : ""}
        <button type="button" data-rem-open>Open Government Relations</button>
      </div>
    </div>`;
  el.querySelector("[data-rem-hide]").addEventListener("click", () => { try { localStorage.setItem(REM_HIDE_KEY, String(d.month)); } catch (e) { /* not remembered */ } el.remove(); });
  el.querySelector("[data-rem-open]").addEventListener("click", () => window.ges && window.ges.showTab("government"));
  el.querySelector("[data-rem-pay]")?.addEventListener("click", () => remPay(d.due_now.map((x) => x.id)));
}

async function refreshReminders() {
  try {
    const r = await fetch("/api/reminders/payments");
    remData = r.ok ? await r.json() : null;
  } catch (e) { remData = null; }
  remPaint();
}

/**
 * Called by Next Month and Fast Forward (app/commands.js): false = the player chose not to end the month yet.
 * Rachit (2026-10-20): when a government investment payment is due, alert on Next Month: pay it, or lose the
 * government's deal. The alert says exactly what would be lost (GET /api/reminders/payments: due_now[].lose).
 */
async function remBeforeMonth() {
  await refreshReminders();
  const d = remData;
  if (!d || !d.due_now.length) return true;
  return remDecide(d);
}

function remDecide(d) {
  return new Promise((resolve) => {
    const back = document.createElement("div");
    back.className = "modal-backdrop";
    const rows = d.due_now.map((x) => `
      <div class="gp-row">
        <div class="gp-head"><b>🏛 ${esc(x.country)}</b><span class="gp-amt">${remM(x.amount)}</span></div>
        ${x.incentive ? `<div class="gp-what">${esc(x.incentive)} deal · investment instalment due now</div>` : ""}
        ${x.lose ? `<div class="gp-lose">If you don't pay: ${esc(x.lose)}.</div>` : ""}
      </div>`).join("");
    back.innerHTML = `<div class="modal gp-modal" role="dialog" aria-label="Government investment payment due">
        <h3>💳 Pay your government investment</h3>
        <p class="gp-sub">You signed ${d.due_now.length > 1 ? "these deals" : "this deal"} and promised to invest. The payment is due before the month ends.
          Pay it to keep the deal, or the government cancels it: the incentive is lost, the remaining payments are cancelled, and trust and brand fall.</p>
        ${rows}
        <div class="gp-cash ${d.short ? "short" : ""}">Your cash: <b>${remM(d.cash)}</b> · due now: <b>${remM(d.due_now_total)}</b>
          ${d.short ? "<br>Not enough cash to pay. Raise money first (Banking & Debt), or you lose the deal." : ""}</div>
        <div class="modal-actions gp-actions">
          ${d.short
            ? `<button type="button" class="primary" data-gp="bank">Raise cash first</button>`
            : `<button type="button" class="primary" data-gp="pay">Pay ${remM(d.due_now_total)} and continue</button>`}
          <button type="button" class="gp-skip" data-gp="skip">Don't pay: lose the deal</button>
          <button type="button" data-gp="back">Go back</button>
        </div>
      </div>`;
    document.body.appendChild(back);
    const done = (ok) => { back.remove(); resolve(ok); };
    back.addEventListener("click", async (e) => {
      const b = e.target.closest("[data-gp]");
      if (!b) return;
      const act = b.dataset.gp;
      if (act === "back") return done(false);
      if (act === "bank") { done(false); if (window.ges) window.ges.showTab("banking"); return; }
      back.querySelectorAll("[data-gp]").forEach((x) => { x.disabled = true; });
      if (act === "pay") {
        const ok = await remPay(d.due_now.map((x) => x.id));
        return done(ok);
      }
      if (act === "skip") {
        if (!confirm("Are you sure? The government cancels the deal and you lose its incentive.")) {
          back.querySelectorAll("[data-gp]").forEach((x) => { x.disabled = false; });
          return;
        }
        for (const x of d.due_now) {
          try { toast((await postJSON("/actions/execute", { action: { type: "gov_skip_instalment", params: { payment_id: x.id } } })).message, true); }
          catch (err) { toast(err.message, true); }
        }
        return done(true);
      }
    });
  });
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  const original = window.refreshAll;
  if (typeof original === "function") {
    window.refreshAll = async function (...args) {
      const out = await original.apply(this, args);
      refreshReminders();
      return out;
    };
  }
  window.gesBeforeMonth = remBeforeMonth;
  window.gesRefreshReminders = refreshReminders;
}

export { refreshReminders, remBeforeMonth };
